/**
 * TrackedCouponCard — wraps CustomerCouponCard with useFeedCardTracking so
 * every page that renders a feed of coupons gets impression/dwell/scroll-past/
 * click tracking for free, without each page having to redefine its own
 * per-card wrapper.
 *
 * feedId/feedSort identify WHICH feed this card is in, for stats grouping —
 * pass something stable per page/section:
 *   - Home "Newest Coupons":   feedId="home_newest_coupons" feedSort="newest"
 *   - Home "Favourites":       feedId="home_favourites"     feedSort="favourites"
 *   - CouponsListAll:          feedId="search"              feedSort=<see below>
 *   - BusinessesDetails:       feedId="business_deals"      feedSort=<grid|list layout, see below>
 *
 * CouponsListAll has no explicit sort control, so there's no single obvious
 * feedSort value the way "newest" is for Home. Passing something like
 * `isFavoritesActive ? 'favourites' : selectedCategories.length > 0 ? 'category' : searchKey.trim() ? 'search_query' : 'browse'`
 * from the caller is a reasonable stand-in for "what filter mode produced
 * this list" — swap it for whatever's actually meaningful to you.
 *
 * BusinessesDetails has no sort control either, and no meaningful concept
 * of one (it's every coupon for one business) — passing the grid/list
 * `effectiveViewMode` toggle as feedSort at least distinguishes the two
 * layouts in the stats; swap for something else if that's not useful.
 *
 * Every prop besides item/index/feedId/feedSort/onPress/businessId is
 * forwarded straight through to CustomerCouponCard as-is (e.g. `layout`,
 * a synthetic `business` object) — this wrapper doesn't need to know the
 * full CustomerCouponCard prop surface for every page that uses it.
 *
 * businessId defaults to `item.business.id`, matching Home/CouponsListAll's
 * data shape. Pass it explicitly when the coupon itself doesn't carry a
 * nested `.business` object but the page already knows which business it
 * is anyway (e.g. BusinessesDetails, where every coupon on the page
 * belongs to the one business at the top).
 *
 * KNOWN TRADE-OFF (same as before): markClicked() fires unconditionally on
 * press, even on the rare press that doesn't actually navigate (e.g. an
 * expired coupon that just shows an alert). See useFeedCardTracking.js.
 */
import React from 'react';
import { CustomerCouponCard } from 'components'; // adjust to match this page's actual import path
import { useFeedCardTracking } from '../../utils/useFeedCardTracking'; // adjust to wherever the hook lands

const TrackedCouponCard = ({
  item,
  index,
  feedId,
  feedSort,
  businessId,
  first,
  isFavourite,
  onPress,
  goToBusinessDetails,
  toggleFavourite,
  ...cardProps
}) => {
  const { cardRef, markClicked } = useFeedCardTracking({
    couponId: item.id,
    businessId: businessId ?? item.business?.id ?? null,
    feedId,
    feedSort,
    feedPosition: index,
  });

  return (
    <CustomerCouponCard
      {...cardProps}
      cardRef={cardRef}
      first={first}
      data={item}
      onPress={() => {
        markClicked();
        onPress();
      }}
      isFavourite={isFavourite}
      goToBusinessDetails={goToBusinessDetails}
      toggleFavourite={toggleFavourite}
    />
  );
};
export default TrackedCouponCard;